import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <Header />
      
      <div className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-[#FF9900] hover:text-orange-400 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <h1 className="text-4xl font-bold mb-6">Terms & Conditions</h1>

          <div className="prose prose-invert prose-orange max-w-none space-y-6 text-gray-300">
            <p className="text-lg">
              By accessing the <strong>Amazon Trademark Registration</strong> website (referred to as the "Site"), you acknowledge and accept the terms and conditions outlined herein, forming an agreement between you and info@amazontrademarkregistration.com (referred to as "we," "us," or "our"). This agreement also incorporates our Privacy Policy as if fully stated here. If you do not agree with these terms, please refrain from using the Site.
            </p>

            <div className="bg-orange-900/20 border-2 border-[#FF9900]/30 rounded-lg p-4 my-6">
              <p className="text-sm">
                <strong>IMPORTANT:</strong> THIS AGREEMENT INCLUDES A MANDATORY ARBITRATION CLAUSE GOVERNED BY THE FEDERAL ARBITRATION ACT, AND A WAIVER OF CLASS ACTIONS.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Conditions for Use of the Site</h2>
              <p>Your access to and use of the Site is contingent upon your acceptance and adherence to these terms. By using the Site, you agree that you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Will comply with these Terms of Service</li>
                <li>Are 18 years of age or older</li>
                <li>Will not use the Site in any way that violates U.S. laws, including its export and re-export regulations</li>
                <li>Will not copy or distribute any content from the Site without our prior written consent</li>
                <li>Will provide accurate details when registering, submitting content, or interacting with the Site</li>
                <li>Will not permit others to use the Site under your user credentials</li>
                <li>Are fully responsible for all activity under your account and user credentials</li>
                <li>Will not use the Site for commercial purposes other than those expressly stated</li>
                <li>Are solely responsible for any content you submit to the Site</li>
                <li>Grant us and other users a non-exclusive license to use, display, reproduce, distribute, or create derivative works from your submitted materials</li>
                <li>Agree that we may remove any of your content and suspend or terminate your account, with or without prior notice</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Your Content</h2>
              <p>
                We reserve the right to publish any content you provide (referred to as "User-Generated Content" or "UGC"). You agree not to upload or use any UGC that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violates the intellectual property rights of others, including copyright, trademarks, or trade secrets</li>
                <li>Infringes on privacy or publicity rights of any third party</li>
                <li>Is unlawful, inaccurate, defamatory, discriminatory, obscene, or harassing</li>
                <li>Encourages criminal activity or civil liability</li>
                <li>Is deemed inappropriate by us, in our sole discretion</li>
                <li>Could harm our company, affiliates, or partners</li>
              </ul>
              <p className="mt-4">
                We are not obligated to remove UGC violating these terms, but we reserve the right to do so at our discretion, and may take further action such as account termination. We are not liable for any harm caused by UGC.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Your Behavior</h2>
              <p>By using the Site, you agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Engage in or promote illegal activities</li>
                <li>Attempt to reverse engineer or disrupt the Site's functionality</li>
                <li>Attempt to access restricted areas of the Site</li>
                <li>Use the Site for unsolicited email campaigns or spam</li>
                <li>Use automated tools to scrape or collect data from the Site</li>
                <li>Interfere with the proper functioning of the Site</li>
                <li>Impersonate another user</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Links to Third-Party Sites</h2>
              <p>
                Our Site may contain links to third-party websites not controlled by us. We assume no responsibility for the content or practices of these external sites. Linking to these sites does not imply endorsement by us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Termination</h2>
              <p>
                We reserve the right to modify or discontinue the Site or any services provided without prior notice. Additionally, we may terminate this agreement for any reason, with or without notice. If we terminate your access due to violations of these terms, you will immediately lose access to the Site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Search Services</h2>
              <p>
                If you use our search services, we employ reasonable methods to identify potential matches to the trademarks you wish to use. However, we cannot guarantee that your trademark will be approved by the USPTO or that it will not face challenges, even with our search services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Trademark Search Scope</h2>
              <p>Our <strong>Amazon Trademark Registration</strong> searches include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Federal Search:</strong> Reviews the USPTO database and includes direct matches, phonetic similarities, or visual design similarities</li>
                <li><strong>Federal, State, and Common Law Search:</strong> Extends to all 50 states, business registries, and domain name databases</li>
                <li><strong>Global Search:</strong> Includes USPTO, state databases, WIPO database, and European Community database</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Disclaimers of Warranty and Limitations of Liability</h2>
              <div className="bg-gray-800 p-6 rounded-lg">
                <p className="text-sm uppercase font-bold mb-4">IMPORTANT LEGAL NOTICE</p>
                <p className="text-sm">
                  WE PROVIDE THE WEBSITE AND RELATED SERVICES "AS IS", "WHERE IS", AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ANY AND ALL EXPRESS OR IMPLIED WARRANTIES. WE MAKE NO GUARANTEE THAT THE WEBSITE WILL MEET YOUR REQUIREMENTS, IS ERROR-FREE, ACCURATE, RELIABLE, WITHOUT INTERRUPTION OR AVAILABLE AT ALL TIMES.
                </p>
                <p className="text-sm mt-4">
                  WE SHALL NOT BE LIABLE FOR ANY INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES ARISING FROM YOUR USE OF, INABILITY TO USE, OR RELIANCE UPON THE WEBSITE IN ANY WAY.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Indemnity</h2>
              <p>
                You agree to defend, indemnify and hold harmless us, our officers, directors, representatives, employees and agents from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses arising from: (i) your violation of any term of these Terms of Service; (ii) your violation of any third party right; or (iii) any claim that any of your content causes damage to a third party.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Dispute Resolution & Arbitration</h2>
              <p>
                For the purposes of this Section, references to "<strong>Amazon Trademark Registration</strong>," "you," and "us" include our respective subsidiaries, affiliates, agents, employees, business partners, predecessors in interest, successors, and assigns.
              </p>
              <p className="mt-4">
                Any dispute shall be FINALLY and EXCLUSIVELY resolved by binding individual arbitration conducted by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules. This arbitration agreement is made pursuant to a transaction involving interstate commerce, and shall be governed by the Federal Arbitration Act.
              </p>
              <div className="bg-orange-900/20 border-2 border-[#FF9900]/30 rounded-lg p-4 my-4">
                <p className="text-sm font-bold">
                  NO CLASS ACTION, OR OTHER REPRESENTATIVE ACTION OR PRIVATE ATTORNEY GENERAL ACTION OR JOINDER OR CONSOLIDATION OF ANY CLAIM WITH A CLAIM OF ANOTHER PERSON SHALL BE ALLOWABLE.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Right to Opt-Out</h2>
              <p>
                You may opt-out of arbitration by notifying us of your decision to reject the arbitration clause in writing. You must notify us in writing within 30 days of accepting this Agreement. Failure to do so will make this Agreement binding on you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Refund Policy</h2>
              <p>
                All packages include attorney-conducted searches and are non-refundable. Once a search and clearance report is issued, payment will be automatically processed within 24-48 hours if we do not receive a response from you via email or phone.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Modifications to the Agreement</h2>
              <p>
                We retain the right to modify or discontinue any of these terms and/or the Privacy Policy at any time, as we deem necessary. We recommend periodically reviewing this section of the Website to stay updated on any modifications. Continued use of the Website after such notice shall be deemed as acceptance of the changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mt-8 mb-4">Contact Information</h2>
              <p>
                If you have any questions regarding these Terms & Conditions, please contact us at:
              </p>
              <div className="bg-gray-800 p-6 rounded-lg mt-4">
                <p><strong>Company:</strong> Amazon Trademark Registration</p>
                <p><strong>Address:</strong> 18952 MacArthur Blvd Suite 100, Irvine, CA 92612, United States</p>
                <p><strong>Phone:</strong> (800) 555-1234</p>
                <p><strong>Email:</strong> info@amazontrademarkregistration.com</p>
              </div>
            </section>

            <p className="text-sm text-gray-400 mt-8 pt-8 border-t border-gray-700">
              <strong>Last Updated:</strong> December 30, 2024
            </p>

            <p className="mt-6">
              By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.
            </p>
          </div>
        </div>
      </div>

      <Footer onOpenContactForm={() => {}} />
    </div>
  );
};

export default TermsConditions;
